import React from 'react';

import i18n from 'util/i18n';

import Subsection from 'components/Section/shared/Subsection';

import { FINANCIAL } from 'config/formSections/financial';

import Show from 'components/Form/Show';
import { FINANCIAL_FEDERAL_DEBT } from 'config/formSections/financial';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { DateSummary, Summary } from 'components/Summary';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import { federalDebtTypes, taxDebtActionOptions } from 'constants/enums/financialOptions';
import NotApplicable from 'components/Form/NotApplicable';
import PVQLocation from 'components/Form/PVQLocation';
import Accordion from 'components/Form/Accordion';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import FederalDebtItem from './FederalDebtItem';

const sectionConfig = {
  key: FINANCIAL_FEDERAL_DEBT.key,
  section: FINANCIAL.name,
  store: FINANCIAL.store,
  subsection: FINANCIAL_FEDERAL_DEBT.name,
  storeKey: FINANCIAL_FEDERAL_DEBT.storeKey,
};

export class FederalDebt extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  // #region Updates
  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      HasFailedToFile: this.props.HasFailedToFile,
      HasFailedToPay: this.props.HasFailedToPay,
      HasOther: this.props.HasOther,
      FailedToFileList: this.props.FailedToFileList,
      FailedToPayList: this.props.FailedToPayList,
      OtherList: this.props.OtherList,
      ...queue,
    });
  };

  /** @param {import('forms/shared/types').ListModel<FederalDebtItem>} values */
  updateFailedFileList = (values) => {
    this.update({
      FailedToFileList: values,
    });
  };

  /** @param {import('forms/shared/types').ListModel<FederalDebtItem>} values */
  updateFailedPayList = (values) => {
    this.update({
      FailedToPayList: values,
    });
  };

  /** @param {import('forms/shared/types').ListModel<FederalDebtItem>} values */
  updateOtherList = (values) => {
    this.update({
      OtherList: values,
    });
  };

  /** @param {import('forms/shared/types').YesOrNoModel} values */
  updateHasFailedFile = (values) => {
    this.update({
      HasFailedToFile: values,
      FailedToFileList: values.value === 'Yes' ? this.props.FailedFileList : {},
    });
  };

  /** @param {import('forms/shared/types').YesOrNoModel} values */
  updateHasFailedPay = (values) => {
    this.update({
      HasFailedToPay: values,
      FailedToPayList: values.value === 'Yes' ? this.props.FailedPayList : {},
    });
  };

  /** @param {import('forms/shared/types').YesOrNoModel} values */
  updateHasOther = (values) => {
    this.update({
      HasOther: values,
      OtherList: values.value === 'Yes' ? this.props.OtherList : {},
    });
  };

  /**
         * Assists in rendering the summary section.
         */
  failedFileSummary = (item, index) => {
    const obj = item.Item || {};
    const date = obj.Date || {};
    const from = DateSummary({ date });
    const year = (obj.Year || {}).year || '';
    const amount = (obj.Amount || {}).value || '';
    const text = `${year ? `${year}` : ''}${amount && year ? ', ' : ''}${amount ? `$${amount}` : ''
    }`.trim();

    return Summary({
      type: i18n.t('financial.pvq.federalDebt.failedToFile.collection.summary.item'),
      index,
      left: text,
      right: from,
      placeholder: i18n.t('financial.pvq.federalDebt.failedToFile.collection.summary.unknown'),
    });
  };

  /**
     * Assists in rendering the summary section.
     */
  failedPaySummary = (item, index) => {
    const obj = item.Item || {};
    const date = obj.Date || {};
    const from = DateSummary({ date });
    const year = (obj.Year || {}).year || '';
    const amount = (obj.Amount || {}).value || '';
    const text = `${year ? `${year}` : ''}${amount && year ? ', ' : ''}${amount ? `$${amount}` : ''
    }`.trim();

    return Summary({
      type: i18n.t('financial.pvq.federalDebt.failedToPay.collection.summary.item'),
      index,
      left: text,
      right: from,
      placeholder: i18n.t('financial.pvq.federalDebt.failedToPay.collection.summary.unknown'),
    });
  };

  /**
 * Assists in rendering the summary section.
 */
  otherSummary = (item, index) => {
    const obj = item.Item || {};
    const date = obj.Date || {};
    const from = DateSummary({ date });
    const year = (obj.Year || {}).year || '';
    const amount = (obj.Amount || {}).value || '';
    const text = `${year ? `${year}` : ''}${amount && year ? ', ' : ''}${amount ? `$${amount}` : ''
    }`.trim();

    return Summary({
      type: i18n.t('financial.pvq.federalDebt.otherPastDue.collection.summary.item'),
      index,
      left: text,
      right: from,
      placeholder: i18n.t('financial.pvq.federalDebt.otherPastDue.collection.summary.unknown'),
    });
  };

  render() {
    const {
      errors,
      scrollIntoView,
      required,
      onError,
      defaultState,
    } = this.props;

    const accordionErrors = (listName) => errors && errors.filter((e) => e.indexOf(`${listName}.accordion`) === 0);

    return (
      <div
        className="section-content federalDebt"
        data-section={FINANCIAL.key}
        data-subsection={FINANCIAL_FEDERAL_DEBT.key}
      >
        <h1 className="section-header">{i18n.t('financial.pvq.destination.federalDebt')}</h1>
        <h4 className="section-header">{i18n.t('financial.pvq.destination.para')}</h4>
        <h4 className="section-header">
          {i18n.t('financial.pvq.destination.paraSecond').split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </h4>
        <Branch
          {...this.props.HasFailedToFile}
          name="has_failed_file"
          label={i18n.t('financial.pvq.federalDebt.failedToFile.title')}
          className="federaldebt-branch"
          labelSize="h4"
          warning
          onUpdate={this.updateHasFailedFile}
          onError={onError}
          required={required}
          scrollIntoView={scrollIntoView}
          attribute="HasFailedToFile"
        />
        {(this.props.HasFailedToFile || {}).value === 'Yes' && (
          <Accordion
            defaultState={defaultState}
            {...this.props.FailedToFileList}
            summary={this.failedFileSummary}
            onUpdate={this.updateFailedFileList}
            onError={onError}
            errors={accordionErrors('FailedToFileList')}
            appendTitle={i18n.t('financial.pvq.federalDebt.failedToFile.append.title')}
            appendLabel={i18n.t('financial.pvq.federalDebt.failedToFile.append.add')}
            required={required}
            scrollIntoView={scrollIntoView}
            attribute="FailedToFileList"
          >
            <FederalDebtItem
              attribute="FailedToFileList"
              activity="failedToFile"
              name="Item"
              bind
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </Accordion>
        )}

        <Branch
          {...this.props.HasFailedToPay}
          value={this.props.HasFailedToPay?.value}
          name="has_failed_pay"
          label={i18n.t('financial.pvq.federalDebt.failedToPay.title')}
          labelSize="h4"
          warning
          onUpdate={this.updateHasFailedPay}
          onError={onError}
          required={required}
          scrollIntoView={scrollIntoView}
          attribute="HasFailedToPay"
        />
        {(this.props.HasFailedToPay || {}).value === 'Yes' && (
          <Accordion
            {...this.props.FailedToPayList}
            defaultState={defaultState}
            summary={this.failedPaySummary}
            onUpdate={this.updateFailedPayList}
            onError={onError}
            errors={accordionErrors('FailedToPayList')}
            appendTitle={i18n.t('financial.pvq.federalDebt.failedToPay.append.title')}
            appendLabel={i18n.t('financial.pvq.federalDebt.failedToPay.append.add')}
            required={required}
            scrollIntoView={scrollIntoView}
            attribute="FailedToPayList"
          >
            <FederalDebtItem
              attribute="FailedToPayList"
              activity="failedToPay"
              name="Item"
              bind
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </Accordion>
        )}

        <Branch
          {...this.props.HasOther}
          name="has_other"
          label={i18n.t('financial.pvq.federalDebt.otherPastDue.title')}
          labelSize="h4"
          warning
          onUpdate={this.updateHasOther}
          onError={onError}
          required={required}
          scrollIntoView={scrollIntoView}
          attribute="HasOther"
        />
        {(this.props.HasOther || {}).value === 'Yes' && (
          <Accordion
            {...this.props.OtherList}
            defaultState={defaultState}
            summary={this.otherSummary}
            onUpdate={this.updateOtherList}
            onError={onError}
            errors={accordionErrors('OtherList')}
            appendTitle={i18n.t('financial.pvq.federalDebt.otherPastDue.append.title')}
            appendLabel={i18n.t('financial.pvq.federalDebt.otherPastDue.append.add')}
            required={required}
            scrollIntoView={scrollIntoView}
            attribute="OtherList"
          >
            <FederalDebtItem
              attribute="OtherList"
              activity="otherPastDue"
              name="Item"
              bind
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </Accordion>
        )}
      </div>
    );
  }
}

FederalDebt.defaultProps = {
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  defaultState: true,
  scrollToBottom: '.bottom-btns',
  errors: [],
};

export default connectSubsection(FederalDebt, sectionConfig);
